import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlertCircle, ArrowUpRight, Check, Copy, RefreshCw, Settings2 } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/common/PageHeader/PageHeader";
import { GenieAvatar } from "@/components/GenieAvatar";
import { Button } from "@/components/ui/button";
import { GenieLoader } from "@/components/ui/GenieLoader";
import { SearchInput } from "@/components/ui/search-input";
import { useUserAgents } from "@/hooks/agent/useAgents";
import { Agent } from "@/types/agent/agent";
import { DEFAULT_BRANDING } from "@/types/ui/branding";
import { resolveGenieAvatarUrl } from "@/utils/image/genieAvatars";
import { geniePageUrl, isPageLive, pageBlockedReason } from "./geniePageStatus";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/**
 * Genie Pages — the owner's view of every public front door they have.
 *
 * One page per Genie, in their branding: the Genie itself, the questions people
 * actually ask, the published guides, the manuals, and every way to reach a
 * person. Everything a visitor does there lands in the Inbox exactly as it
 * does today.
 */
const GeniePagesPage = () => {
  const navigate = useNavigate();
  const { data: agents = [], isLoading, isError, refetch, isFetching } = useUserAgents(100);
  const [searchTerm, setSearchTerm] = useState("");

  const filteredAgents = useMemo(() => {
    if (!searchTerm) return agents;
    const query = searchTerm.toLowerCase();
    return agents.filter((agent: Agent) => agent.name?.toLowerCase().includes(query));
  }, [agents, searchTerm]);

  const liveCount = useMemo(
    () => agents.filter((agent: Agent) => isPageLive(agent)).length,
    [agents],
  );

  const header = (
    <PageHeader
      title="Genie Pages"
      genieIcon="web-page"
      subtitle="One public page per Genie — ask, call, read the guides, download the manuals. Everything lands in your Inbox."
    />
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto max-w-[1920px] px-4 py-6 md:py-8">
          {header}
          <GenieLoader size="block" loadingText="Loading your Genie Pages…" className="py-16" />
        </div>
      </div>
    );
  }

  // A failed fetch must never be dressed up as "you have no pages".
  if (isError) {
    return (
      <div className="min-h-screen bg-[#F9FAFC]">
        <div className="container mx-auto max-w-[1920px] px-4 py-6 md:py-8">
          {header}
          <div className="mx-auto max-w-md rounded-2xl border border-gray-200 bg-white px-6 py-10 text-center">
            <AlertCircle className="mx-auto mb-3 h-8 w-8 text-red-600" />
            <h2 className="text-lg font-semibold text-gray-900">We couldn't load your Genies</h2>
            <p className="mt-1 text-sm text-gray-600">
              Your pages are fine — we just couldn't reach them right now.
            </p>
            <Button
              variant="outline"
              className="mt-5"
              onClick={() => void refetch()}
              disabled={isFetching}
            >
              <RefreshCw className={`mr-2 h-4 w-4 ${isFetching ? "animate-spin" : ""}`} />
              Try again
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9FAFC]">
      <div className="container mx-auto max-w-[1920px] px-4 py-6 md:py-8">
        {header}

        {agents.length > 0 ? (
          <>
            <div className="mb-6 flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
              <div className="text-lg font-semibold text-gray-800">
                Your pages
                <span className="ml-2 text-sm font-normal text-gray-500">
                  {liveCount} of {agents.length} live
                </span>
              </div>
              <div className="rounded-xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] p-[2px]">
                <div className="rounded-[10px] bg-white">
                  <SearchInput
                    value={searchTerm}
                    onChange={setSearchTerm}
                    placeholder="Search Genies..."
                    className="w-64"
                    inputClassName="border-transparent focus-visible:ring-0"
                  />
                </div>
              </div>
            </div>

            {filteredAgents.length > 0 ? (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3 md:gap-6">
                {filteredAgents.map((agent: Agent) => (
                  <GeniePageCard key={agent.id} agent={agent} />
                ))}
              </div>
            ) : (
              <div className="py-12 text-center">
                <p className="mb-4 text-gray-500">No Genies match your search</p>
                <Button variant="outline" onClick={() => setSearchTerm("")}>
                  Clear search
                </Button>
              </div>
            )}
          </>
        ) : (
          <div className="py-16 text-center">
            <div className="mx-auto mb-6 flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#4E9CFF]/20 to-[#FF4DB8]/20">
              <GenieIcon name="globe" size={48} className="h-12 w-12" />
            </div>
            <h2 className="mb-2 text-xl font-bold text-gray-900 md:text-2xl">No Genies yet</h2>
            <p className="mx-auto mb-6 max-w-md px-4 text-sm text-gray-600 md:mb-8 md:text-base">
              Create your first Genie and it gets a page of its own — branded, public, and ready to
              hand out.
            </p>
            <Button
              onClick={() => navigate("/genie-hub/my-genies")}
              className="inline-flex items-center rounded-xl px-8 py-4 font-semibold text-white shadow-lg transition-all hover:opacity-90"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              Go to My Genies
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const GeniePageCard = ({ agent }: { agent: Agent }) => {
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const brand = agent.branding?.primaryColor || DEFAULT_BRANDING.primaryColor;
  const hasCustomBranding = Boolean(
    agent.branding?.primaryColor && agent.branding.primaryColor !== DEFAULT_BRANDING.primaryColor,
  );
  const url = geniePageUrl(agent);
  const live = isPageLive(agent);
  const blockedReason = pageBlockedReason(agent);

  const handleCopy = async () => {
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      toast.success("Page link copied");
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy the link — you can select it from the page instead.");
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white p-5 transition-all hover:shadow-lg md:p-6">
      <div className="flex items-center gap-4">
        <div className="h-12 w-12 flex-shrink-0 md:h-14 md:w-14">
          <GenieAvatar
            size={56}
            color={brand}
            customAvatarUrl={resolveGenieAvatarUrl(agent)}
            secondaryColor={agent.branding?.secondaryColor}
            gradientEnabled={agent.branding?.gradientEnabled}
            gradientAngle={agent.branding?.gradientAngle}
            avatarBackgroundOpacity={agent.branding?.avatarBackgroundOpacity}
            hasCustomBranding={hasCustomBranding}
          />
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-semibold text-gray-900">{agent.name}</h3>
          <p className="mt-0.5 truncate text-sm text-gray-500">
            {url ? url.replace(/^https?:\/\//, "") : "No page address yet"}
          </p>
        </div>
        <span
          className={`flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide ${
            live ? "bg-[#EBF5FF] text-[#2563EB]" : "bg-gray-100 text-gray-500"
          }`}
        >
          {live ? "Live" : "Not public"}
        </span>
      </div>

      {/* Being explicit about why beats a disabled button with no explanation. */}
      {blockedReason && (
        <p className="rounded-xl bg-gray-50 px-4 py-3 text-[13px] leading-snug text-gray-600">
          {blockedReason}
        </p>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          className="gap-1.5"
          onClick={() => navigate(`/genie-hub/genie-pages/${agent.id}`)}
        >
          <Settings2 className="h-4 w-4" />
          Design page
        </Button>
        {live && url && (
          <>
            <a
              href={url}
              target="_blank"
              rel="noreferrer noopener"
              className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl px-4 py-2.5 text-sm font-semibold text-white transition-opacity hover:opacity-90"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              View page
              <ArrowUpRight className="h-4 w-4" />
            </a>
            <Button variant="outline" className="gap-1.5" onClick={() => void handleCopy()}>
              {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
              Copy link
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export default GeniePagesPage;
